<template>
  <form
    v-on:submit.prevent="onSubmit"
    class="form-vendor">
    <input-text
      v-bind:maxlength="255"
      placeholder="Наименование"
      title="Наименование"
      v-bind:violations="nameInputErrors"
      v-letter-capital
      v-model="name" />
    <input-text
      v-bind:maxlength="255"
      placeholder="Контактное лицо"
      title="Контактное лицо"
      v-bind:violations="contactInputErrors"
      v-letter-capital
      v-model="contact" />
    <input-phone
      title="Телефон"
      v-bind:violations="phoneInputErrors"
      v-model="phone"
    />
    <input-email
      v-bind:required="false"
      label="E-mail"
      placeholder="E-mail"
      v-bind:violations="emailInputErrors"
      v-model="email"
    />
    <input-text
      v-bind:maxlength="255"
      placeholder="Адрес"
      title="Адрес"
      v-bind:violations="addressInputErrors"
      v-letter-capital
      v-model="address" />
    <div class="form-vendor__buttons">
      <button
        class="form-vendor__button-cancel"
        type="button"
        v-on:click="onClose">Отмена</button>
      <button
        class="form-vendor__button-save"
        type="submit">
        Сохранить
      </button>
    </div>
  </form>
</template>

<script>
import InputText from '@/components/InputText.vue';
import InputEmail from '@/components/InputEmail.vue';
import InputPhone from '@/components/InputPhone.vue';
import { VendorRequest } from '@/storage/gateways/VendorGateway';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Название события подтверждения.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Название события закрытия.
 * @type {string}
 */
const NAME_EVENT_CLOSE = 'close';

/**
 * @description Форма поставщика.
 * @type {any}
 */
export default {
  name: 'FormVendor',
  components: {
    InputPhone,
    InputEmail,
    InputText,
  },
  computed: {
    nameInputErrors: resolveInputErrors('name'),
    contactInputErrors: resolveInputErrors('contact'),
    phoneInputErrors: resolveInputErrors('phone'),
    emailInputErrors: resolveInputErrors('email'),
    addressInputErrors: resolveInputErrors('address'),
    /**
     * @description Редактируется.
     * @returns {boolean}
     */
    isEdit() {
      return this.vendorId !== '';
    },
  },
  data() {
    return {
      name: this.initialName,
      contact: this.initialContact,
      phone: this.initialPhone,
      email: this.initialEmail,
      address: this.initialAddress,
    };
  },
  methods: {
    /**
     * @description Обработать закрытие формы.
     * @returns {void}
     */
    onClose() {
      this.$emit(NAME_EVENT_CLOSE);
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      this.$emit(NAME_EVENT_SUBMIT, request);
    },
    /**
     * @description Создать запрос.
     * @returns {VendorRequest}
     */
    makeRequest() {
      return new VendorRequest(
        this.vendorId ?? null,
        this.name,
        this.contact,
        this.phone,
        this.email,
        this.address,
      );
    },
  },
  props: {
    /**
     * @description Идентификатор поставщика.
     */
    vendorId: { type: String, default: () => '' },
    /**
     * @description Начальное наименование.
     */
    initialName: { type: String, default: () => '' },
    /**
     * @description Начальное контактное лицо.
     */
    initialContact: { type: String, default: () => '' },
    /**
     * @description Начальный телефон.
     */
    initialPhone: { type: String, default: () => '' },
    /**
     * @description Начальная почта.
     */
    initialEmail: { type: String, default: () => '' },
    /**
     * @description Начальный адрес.
     */
    initialAddress: { type: String, default: () => '' },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
button {
  outline: none !important;
}
.colorItem:focus,
.vendor-form__icon__image-select > button:focus {
  border: 2px solid #c2cfe0;
  border-radius: 50%;
}
.form-vendor {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-top: 20px;
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
  }
  &__exit {
    position: absolute;
    right: -10px;
    top: 0px;
    background-image: url("@/assets/img/exit-black.svg");
    background-position: center;
    background-size: auto;
    background-repeat: no-repeat;
    background-color: #eaeff3;
    padding: 9px;
    border-radius: 2px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    &:hover {
      background-color: transparent;
    }
  }
  &__icon {
    &__caption {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192a3e;
      margin: 0 0 10px 0;
    }
    &__colors {
      flex: 1;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }
    &__color {
      min-width: 36px;
      width: 36px;
      height: 36px;
      min-height: 36px;
      border-radius: 50%;
      margin: 0 10px 10px 0;
      cursor: pointer;
      transition: border-width 0.1s;
      &.outline {
        border: 4px solid #c2cfe0;
      }
      &:hover {
        border: 4px solid #c2cfe0;
      }
    }

    &__one-row {
      display: flex;
      flex-direction: row;
    }
    &__text {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: var(--unfocus-color);
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 30px 0 0 auto;
  }
  &__button {
    &-cancel {
      @include buttons.btn-transparent(100%, 3rem, 1rem);
    }
    &-save {
      @include buttons.btn-blue(100%, 3rem, 1rem);
    }
  }
}
.validate-error {
  padding: 3px;
  color: red;
  font-size: 0.8rem;
}
</style>
